import { useTheme } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { defaultLayout, setSelectedDateFromPlotClick } from "../plotting";
import { SavingsTimeseriesPoint, UnitType } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";

interface SavingsPlotProps {
  data: SavingsTimeseriesPoint[];
  includeAlloy: boolean;
  setSelectedDate: (date: Date) => void;
  showSpecific: boolean;
}

export const SavingsPlot = ({
  data,
  includeAlloy,
  setSelectedDate,
  showSpecific,
}: SavingsPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();
  const theme = useTheme();

  const positiveColor = theme.palette.success.main;
  const negativeColor = theme.palette.error.main;
  const formatLabel = formatNumber(showSpecific ? 2 : 0);

  const plotData = useMemo(
    () =>
      buildFigData(
        data,
        includeAlloy,
        showSpecific,
        t,
        positiveColor,
        negativeColor,
        formatLabel
      ),
    [
      data,
      includeAlloy,
      showSpecific,
      t,
      positiveColor,
      negativeColor,
      formatLabel,
    ]
  );
  const layout = useMemo(
    () => buildLayout(showSpecific, t, units),
    [showSpecific, t, units]
  );

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={plotData}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
    </div>
  );
};

const buildFigData = (
  data: SavingsTimeseriesPoint[],
  includeAlloy: boolean,
  showSpecific: boolean,
  t: (key: string) => string,
  positiveColor: string,
  negativeColor: string,
  formatLabel: (value?: number | null) => string
): Partial<PlotData>[] => {
  const allData = data.filter((d) => d.standard === "all");
  const dates = allData.map((d) => d.period_start);
  const values = allData.map((d) => {
    const alloySavings = includeAlloy ? d.alloy_savings : 0;
    if (showSpecific) {
      // Blend from totals so the alloy heats' own mass is in the denominator;
      // the alloy heats are a separate population from the scrap-savings heats.
      const chargedMass =
        d.total_charged_mass + (includeAlloy ? d.alloy_charged_mass : 0);
      if (chargedMass <= 0) return d.specific_savings;
      return (d.total_savings + alloySavings) / chargedMass;
    }
    return d.total_savings + alloySavings;
  });
  const barColors = values.map((value) =>
    (value ?? 0) >= 0 ? positiveColor : negativeColor
  );
  const labels = values.map((value) =>
    value === null ? "" : formatLabel(value)
  );

  return [
    {
      x: dates,
      y: values,
      type: "bar",
      name: t("savings"),
      marker: { color: barColors },
      text: labels,
      textposition: "outside",
      cliponaxis: false,
    },
  ];
};

const buildLayout = (
  showSpecific: boolean,
  t: (key: string) => string,
  units: (unitType: UnitType) => string
): Partial<Layout> => ({
  ...defaultLayout,
  height: 500,
  yaxis: {
    title: {
      text: `${t("savings")}${units(showSpecific ? "specific_cost" : "cost")}`,
    },
  },
  xaxis: {
    title: { text: t("date") },
  },
});
